<template>
  <span>
    <button v-if="status === 'ACTIVE'" class="btn btn-outline-warning" data-bs-toggle="modal" :data-bs-target="`#${modalId}`">
      <i class="fa-solid fa-lock"></i> Freeze
    </button>
    <button v-else-if="status === 'FROZEN' || status === 'SUSPENDED'" class="btn btn-outline-success" data-bs-toggle="modal" :data-bs-target="`#${modalId}`">
      <i class="fa-solid fa-lock-open"></i> Unfreeze
    </button>

    <div class="modal fade" :id="modalId" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isFreezing ? 'Freeze' : 'Unfreeze' }} Wallet</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small">
              A freeze blocks new credits and debits, but does not stop a ride already under way and
              does not block an approved refund from executing.
            </p>
            <label class="form-label">Reason <span class="text-danger">*</span></label>
            <textarea class="form-control" rows="3" v-model="reason" required></textarea>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn" :class="isFreezing ? 'btn-warning' : 'btn-success'"
                    :disabled="isSaving || !reason.trim()" @click="submit">
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
              {{ isSaving ? 'Saving...' : (isFreezing ? 'Freeze Wallet' : 'Unfreeze Wallet') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </span>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Modal } from 'bootstrap';
import { showToast, notificationType } from '../utility/toastNotificationUtility';

const props = defineProps({
  walletId: { type: String, required: true },
  status: { type: String, default: null }
});

const emit = defineEmits(['updated']);

const modalId = computed(() => `wallet-freeze-modal-${props.walletId}`);
const isFreezing = computed(() => props.status === 'ACTIVE');
const reason = ref('');
const isSaving = ref(false);

const submit = async () => {
  if (!reason.value.trim() || isSaving.value) return;
  isSaving.value = true;
  try {
    const action = isFreezing.value ? 'freeze' : 'unfreeze';
    const response = await fetch(`/api/admin/wallets/${props.walletId}/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason.value.trim() })
    });
    if (!response.ok) {
      const message = await response.text();
      showToast({ title: 'Error', body: message || `Unable to ${action} wallet.`, type: notificationType.ERROR, delay: 5000 });
      return;
    }
    const updated = await response.json();
    reason.value = '';
    const modalEl = document.getElementById(modalId.value);
    const instance = Modal.getInstance(modalEl) || new Modal(modalEl);
    instance.hide();
    showToast({ title: 'Success', body: `Wallet ${isFreezing.value ? 'frozen' : 'unfrozen'}.`, type: notificationType.SUCCESS });
    emit('updated', updated);
  } catch (error) {
    console.error('Error changing wallet freeze state:', error);
    showToast({ title: 'Oops..', body: 'Encountered an internal error. Please contact support.', type: notificationType.ERROR, delay: 7000 });
  } finally {
    isSaving.value = false;
  }
};
</script>
